import {
	DateField,
	Drawer,
	DrawerBody,
	DrawerFooter,
	DrawerFooterActions,
	DrawerHeader,
	DrawerHeading,
	FormDrawerForm,
	SubmitButton,
	TextField,
	TimeField,
} from "@dgs/core";
import { isBefore, isSameDay } from "date-fns";
import { Formik } from "formik";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AirportSingleSelectField } from "./AirportSingleSelectField";
import { ChoiceButtonBaseIcon, ChoiceButtonGroup } from "./ChoiceButton";
import { TravelTypeButton } from "./TravelTypeButton";
import { GuestTravelInquiry } from "./types/travelInquiry";

interface Props {
	onSubmit: (formValues: GuestTravelInquiry) => void;
	inquiry: GuestTravelInquiry | null;
}

const PaddedChoiceButtonGroup = styled(ChoiceButtonGroup)`
	padding-bottom: ${({ theme }) => theme.spacing(4)};
`;

export const TravelInquiryDrawerContent: FC<Props> = ({ onSubmit, inquiry }) => {
	const { t } = useTranslation();

	const initialValues: GuestTravelInquiry = inquiry || {
		type: "flight",
		arrivalLocation: null,
		departureLocation: null,
		earliestDepartureDate: "",
		earliestDepartureTime: "",
		latestArrivalDate: "",
		latestArrivalTime: "",
		remarks: "",
	};

	const validate = useCallback(
		(values: GuestTravelInquiry) => {
			const errors: { [T in keyof GuestTravelInquiry]?: string } = {};

			if (!values.latestArrivalDate) {
				errors.latestArrivalDate = t("Field is required");
			}
			if (!values.latestArrivalTime) {
				errors.latestArrivalTime = t("Field is required");
			}
			if (!values.earliestDepartureDate) {
				errors.earliestDepartureDate = t("Field is required");
			}
			if (!values.earliestDepartureTime) {
				errors.earliestDepartureTime = t("Field is required");
			}
			if (
				values.latestArrivalDate &&
				values.earliestDepartureDate &&
				values.latestArrivalTime &&
				values.earliestDepartureTime
			) {
				if (isBefore(values.latestArrivalDate, values.earliestDepartureDate)) {
					errors.earliestDepartureDate = t("The earliest departure must be before the latest arrival.");
				} else if (
					isSameDay(values.earliestDepartureDate, values.latestArrivalDate) &&
					values.latestArrivalTime < values.earliestDepartureTime
				) {
					errors.earliestDepartureTime = t("The earliest departure must be before the latest arrival.");
				}
			}
			if (!values.arrivalLocation) {
				errors.arrivalLocation = t("Field is required");
			}
			if (!values.departureLocation) {
				errors.departureLocation = t("Field is required");
			}

			return errors;
		},
		[t],
	);

	return (
		<Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
			{({ values, setFieldValue }) => (
				<FormDrawerForm>
					<DrawerBody>
						<PaddedChoiceButtonGroup>
							<TravelTypeButton
								type="button"
								aria-label={t("Create flight inquiry")}
								onClick={() => {
									void setFieldValue("type", "flight");
									void setFieldValue("arrivalLocation", null);
									void setFieldValue("departureLocation", null);
								}}
								aria-pressed={values.type === "flight"}
							>
								<ChoiceButtonBaseIcon src="/icons/travel.svg" />
								{t("flight")}
							</TravelTypeButton>
							<TravelTypeButton
								type="button"
								aria-label={t("Create train inquiry")}
								onClick={() => {
									void setFieldValue("type", "train");
									void setFieldValue("arrivalLocation", "");
									void setFieldValue("departureLocation", "");
								}}
								aria-pressed={values.type === "train"}
							>
								<ChoiceButtonBaseIcon src="/icons/train.svg" />
								{t("train")}
							</TravelTypeButton>
						</PaddedChoiceButtonGroup>
						<DateField name="earliestDepartureDate" label={t("Earliest departure date")} required={true} />
						<TimeField name="earliestDepartureTime" label={t("Earliest departure time")} required={true} />
						<DateField name="latestArrivalDate" label={t("Latest arrival date")} required={true} />
						<TimeField name="latestArrivalTime" label={t("Latest arrival time")} required={true} />
						{values.type === "flight" ? (
							<AirportSingleSelectField name="departureLocation" label={t("Departure location")} required={true} />
						) : (
							<TextField name="departureLocation" label={t("Departure location")} required={true} />
						)}
						{values.type === "flight" ? (
							<AirportSingleSelectField name="arrivalLocation" label={t("Arrival location")} required={true} />
						) : (
							<TextField name="arrivalLocation" label={t("Arrival location")} required={true} />
						)}
						<TextField name="remarks" label={t("Remarks")} />
					</DrawerBody>
					<DrawerFooter>
						<DrawerFooterActions />
						<SubmitButton>{t("Save")}</SubmitButton>
					</DrawerFooter>
				</FormDrawerForm>
			)}
		</Formik>
	);
};

interface DrawerProps {
	travelInquiry: GuestTravelInquiry;
	open: boolean;
	handleClose: () => void;
	onSubmit: (formValues: GuestTravelInquiry) => void;
}

export const TravelInquiryEditDrawer: FC<DrawerProps> = ({ open, handleClose, travelInquiry, onSubmit }) => {
	const { t } = useTranslation();

	const handleSubmit = useCallback(
		(values: GuestTravelInquiry) => {
			onSubmit(values);
			handleClose();
		},
		[handleClose, onSubmit],
	);

	if (!travelInquiry) return null;

	return (
		<Drawer open={open} onClose={handleClose}>
			<DrawerHeader onClose={handleClose}>
				<DrawerHeading>{t("Edit inquiry")}</DrawerHeading>
			</DrawerHeader>
			<TravelInquiryDrawerContent onSubmit={handleSubmit} inquiry={travelInquiry} />
		</Drawer>
	);
};
